import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/ui/components/common/Dialog'
import { Button } from '@/ui/components/common/Button'
import type { TranslationImportResult } from '@/domains/types'

interface TranslationImportResultDialogProps {
  result: TranslationImportResult | null
  onOpenChange: (open: boolean) => void
}

/** Shown after a translation import completes — the four counters, plus
 * scrollable skipped/errors lists when either is non-empty (import is
 * tolerant: a deleted entity is skipped, a bad value is an error, and
 * neither aborts the rest of the batch). */
export function TranslationImportResultDialog({
  result,
  onOpenChange,
}: TranslationImportResultDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={result !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('translations.dashboard_import_result_title')}</DialogTitle>
        </DialogHeader>

        {result && (
          <div className="space-y-4">
            <p className="text-sm">
              {t('translations.dashboard_import_updated', {
                investigations: result.investigationsUpdated,
                leads: result.leadsUpdated,
                facts: result.factsUpdated,
              })}
            </p>

            {result.skipped.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2">
                  {t('translations.dashboard_import_skipped_title')}
                </h4>
                <ul className="text-sm space-y-1 list-disc pl-4 opacity-90 max-h-[200px] overflow-y-auto">
                  {result.skipped.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2 text-destructive">
                  {t('translations.dashboard_import_errors_title')}
                </h4>
                <ul className="text-sm space-y-1 list-disc pl-4 text-destructive opacity-90 max-h-[200px] overflow-y-auto">
                  {result.errors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('translations.dashboard_import_close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
